import { Box, Paper } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import Typography from "components/basic/typography";
import { AnalyticsTimeseriesQuery } from "generated/graphql";
import { SERIES_COLORS } from "../../constants";
import { formatDay, formatNumber } from "../../helpers/format";

interface TrafficChartProps {
  points: AnalyticsTimeseriesQuery["analyticsTimeseries"];
  isLoading: boolean;
}

/** Visitors and page views over time. Two series, so the legend is required. */
const TrafficChart: React.FC<TrafficChartProps> = ({ points, isLoading }) => (
  <Paper variant="outlined" sx={{ padding: 2, borderRadius: 2 }}>
    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
      Traffic
    </Typography>
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      Daily visitor counts are exact — each day has its own salt.
    </Typography>

    <Box sx={{ width: "100%", minWidth: 0 }}>
      <LineChart
        height={320}
        loading={isLoading}
        xAxis={[
          {
            scaleType: "point",
            data: points.map((point) => point.date),
            valueFormatter: formatDay,
            disableTicks: true,
          },
        ]}
        yAxis={[
          {
            disableLine: true,
            disableTicks: true,
            valueFormatter: (value: number) => formatNumber(value),
          },
        ]}
        series={[
          {
            label: "Visitors",
            data: points.map((point) => point.visitors),
            color: SERIES_COLORS.visitors,
            curve: "linear",
            showMark: points.length <= 31,
            valueFormatter: (value) => formatNumber(value ?? 0),
          },
          {
            label: "Page views",
            data: points.map((point) => point.pageviews),
            color: SERIES_COLORS.pageviews,
            curve: "linear",
            showMark: points.length <= 31,
            valueFormatter: (value) => formatNumber(value ?? 0),
          },
        ]}
        margin={{ left: 60, right: 16, top: 48, bottom: 32 }}
        slotProps={{
          legend: {
            direction: "row",
            position: { horizontal: "left", vertical: "top" },
            itemMarkWidth: 10,
            itemMarkHeight: 10,
          },
          noDataOverlay: { message: "No traffic recorded in this range" },
        }}
      />
    </Box>
  </Paper>
);

export default TrafficChart;
